/**
 * VÉRAULT Travels - Enhanced Trip AI Assistant
 * 
 * HARDENED FIX: AI Proposal Formatting & Rendering Contract
 * 
 * CORE RULES:
 * 1. SEPARATE CHAT FROM PROPOSALS - Two distinct layers, never mixed
 * 2. PROPOSAL GROUP CONTAINER - Grouped by day with metadata
 * 3. TYPED PROPOSAL CARDS - lodging, restaurant, activity, transfer
 * 4. CONSISTENT ACTION BAR - Approve/Edit/Reject on every card
 * 5. RESPONSIVE LAYOUT - Stack vertically on mobile
 * 6. APPLY BEHAVIOR - Writes to itinerary immediately
 * 7. FALLBACK SAFETY - Invalid proposals render as plain text
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Loader2, Send, Sparkles, Copy, Check, ChevronDown, ChevronUp,
  MapPin, Clock, DollarSign, Calendar, ArrowRight, RefreshCw, Trash2,
  FileText, Plane, Hotel, Utensils, Eye, AlertCircle, X,
  Lightbulb, History, ExternalLink, ListChecks, Plus, Replace,
  Users, Car, MessageSquare, Map as MapIcon, Lock, Unlock, ChevronLeft, ChevronRight
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { 
  ProposalGroup,
  ProposalCard as StructuredProposalCard,
  ProposalFallback,
  groupProposalsByDay 
} from './AIProposalSystem';
import { formatDayHeader, formatDayShort, getDayDate, calculateTripDays } from '../contexts/TripPlanningContext';
import { getTripStartDate, getTripEndDate, getTripDestinationsDisplay, formatDate as formatDateUtil } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Deep link map for navigation
const DEEP_LINK_LABELS = {
  command_center: "Command Center",
  trip_details: "Trip Details",
  itinerary: "Itinerary",
  logistics: "Logistics",
  client_lounge: "Client Lounge",
  agreements: "Agreements",
  advisor_notes: "Advisor Notes",
  dates: "Dates",
  destination: "Destination",
  travelers: "Travelers",
  flights: "Flights",
  lodging: "Lodging",
  dining: "Dining",
  activities: "Activities"
};

// Markdown components for rendering
const markdownComponents = {
  h1: ({ children }) => <h1 className="text-lg font-serif text-[#1A1A1A] mt-4 mb-2 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-medium text-[#1A1A1A] mt-3 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-medium text-[#1A1A1A] mt-2 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-[#595959] mb-2 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-[#595959]">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1A1A1A]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#C8A85A] pl-3 italic text-[#8C8C8C]">{children}</blockquote>,
  code: ({ children }) => <code className="bg-[#F5F1EB] px-1 rounded text-xs">{children}</code>,
};

// Conflict Resolution Modal Component
function ConflictModal({ isOpen, onClose, onConfirm, existingContent, proposalCount, loading }) {
  const [selectedMode, setSelectedMode] = useState('append');
  
  if (!isOpen) return null;
  
  const conflictModes = [
    {
      id: 'append',
      label: 'Add to Existing',
      description: 'Keep current itinerary and add new items',
      icon: Plus,
      recommended: true
    },
    {
      id: 'replace_section',
      label: 'Replace Sections',
      description: 'Replace only the sections with new items',
      icon: Replace,
      recommended: false
    },
    {
      id: 'replace_all',
      label: 'Replace Everything',
      description: 'Clear entire itinerary and start fresh',
      icon: Trash2,
      recommended: false,
      dangerous: true
    }
  ];
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        {/* Header */}
        <div className="bg-[#C8A85A]/10 px-6 py-4 border-b border-[#E6E4DE]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C8A85A]/20 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-[#C8A85A]" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-[#1A1A1A]">Existing Itinerary Detected</h3>
              <p className="text-sm text-[#8C8C8C]">Choose how to apply {proposalCount} item{proposalCount !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
        
        {/* Existing Content Summary */}
        {existingContent && (
          <div className="px-6 py-3 bg-[#F9F8F4] border-b border-[#E6E4DE]">
            <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-2">Current Content</p>
            <div className="flex flex-wrap gap-3 text-xs">
              {existingContent.hotels > 0 && (
                <span className="flex items-center gap-1 text-[#595959]">
                  <Hotel className="w-3 h-3" /> {existingContent.hotels} hotel{existingContent.hotels !== 1 ? 's' : ''}
                </span>
              )}
              {existingContent.flights > 0 && (
                <span className="flex items-center gap-1 text-[#595959]">
                  <Plane className="w-3 h-3" /> {existingContent.flights} flight{existingContent.flights !== 1 ? 's' : ''}
                </span>
              )}
              {existingContent.transfers > 0 && (
                <span className="flex items-center gap-1 text-[#595959]">
                  <ArrowRight className="w-3 h-3" /> {existingContent.transfers} transfer{existingContent.transfers !== 1 ? 's' : ''}
                </span>
              )}
              {existingContent.itinerary_days > 0 && (
                <span className="flex items-center gap-1 text-[#595959]">
                  <Calendar className="w-3 h-3" /> {existingContent.itinerary_days} day{existingContent.itinerary_days !== 1 ? 's' : ''} planned
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Options */}
        <div className="p-6 space-y-3">
          {conflictModes.map(mode => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setSelectedMode(mode.id)}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  selectedMode === mode.id
                    ? 'border-[#C8A85A] bg-[#C8A85A]/5'
                    : 'border-[#E6E4DE] hover:border-[#C8A85A]/50'
                } ${mode.dangerous ? 'hover:border-[#B37E6B]' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedMode === mode.id
                      ? mode.dangerous ? 'bg-[#B37E6B]/20' : 'bg-[#C8A85A]/20'
                      : 'bg-[#F5F1EB]'
                  }`}>
                    <Icon className={`w-4 h-4 ${
                      selectedMode === mode.id
                        ? mode.dangerous ? 'text-[#B37E6B]' : 'text-[#C8A85A]'
                        : 'text-[#8C8C8C]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        mode.dangerous ? 'text-[#B37E6B]' : 'text-[#1A1A1A]'
                      }`}>
                        {mode.label}
                      </span>
                      {mode.recommended && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8DA399] text-white uppercase tracking-wide">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#8C8C8C] mt-0.5">{mode.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMode === mode.id
                      ? 'border-[#C8A85A] bg-[#C8A85A]'
                      : 'border-[#D1D1D1]'
                  }`}>
                    {selectedMode === mode.id && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Actions */}
        <div className="px-6 py-4 bg-[#F9F8F4] border-t border-[#E6E4DE] flex gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selectedMode)}
            disabled={loading}
            className="flex-1 bg-[#1A1A1A] hover:bg-[#2C2C2C] text-white"
            data-testid="confirm-bulk-apply-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <ListChecks className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Applying...' : 'Apply All'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// Context Snippet Selector
function ContextSnippets({ selected, onChange }) {
  const snippets = [
    { id: 'itinerary', label: 'Itinerary', icon: Calendar },
    { id: 'logistics', label: 'Logistics', icon: Plane },
    { id: 'travelers', label: 'Travelers', icon: FileText },
  ];

  return (
    <div className="flex gap-2 mb-2">
      {snippets.map(s => {
        const Icon = s.icon;
        const isSelected = selected.includes(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              if (isSelected) {
                onChange(selected.filter(id => id !== s.id));
              } else {
                onChange([...selected, s.id]);
              }
            }}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${
              isSelected 
                ? 'bg-[#C8A85A] text-white' 
                : 'bg-[#F5F1EB] text-[#8C8C8C] hover:bg-[#EEE9E2]'
            }`}
          >
            <Icon className="w-3 h-3" />
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// PROPOSAL RENDERING LAYER (Uses AIProposalSystem components)
// ============================================================

// Proposals Panel - Separate container for all proposals (NEVER mixed with chat)
function ProposalsPanel({
  proposals,
  planningState,
  onApproveItem,
  onRejectItem,
  onEditItem,
  onPromoteItem,
  onSuppressItem,
  onReplaceItem,
  onApplyDay,
  onApplyAll,
  applyingItem,
  applyingDay,
  applyingAll
}) {
  // Group proposals by day for organized display
  const proposalGroups = groupProposalsByDay(proposals);
  const currentDay = planningState?.current_working_day || 1;
  
  // Filter out suppressed proposals
  const activeProposals = proposals.filter(p => !p.suppressed);
  
  if (activeProposals.length === 0) return null;
  
  // Check for any malformed proposals that need fallback rendering
  const validProposals = activeProposals.filter(p => p.type && p.data);
  const invalidProposals = activeProposals.filter(p => !p.type || !p.data);
  
  return (
    <div className="space-y-4" data-testid="proposals-panel">
      {/* HEADER with count and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#C8A85A]/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#C8A85A]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-[#1A1A1A]">AI Proposals</h3>
            <p className="text-xs text-[#8C8C8C]">{activeProposals.length} pending • Collapsed by default</p>
          </div>
        </div>
      </div>
      
      {/* PROPOSAL GROUPS BY DAY (Category-based inside each group) */}
      {proposalGroups.map((group) => (
        <ProposalGroup
          key={group.groupId}
          groupId={group.groupId}
          dayIndex={group.dayIndex}
          templateType={group.templateType}
          status={group.status}
          proposals={group.proposals.filter(p => !p.suppressed)}
          onApproveItem={onApproveItem}
          onRejectItem={onRejectItem}
          onEditItem={onEditItem}
          onPromoteItem={onPromoteItem}
          onSuppressItem={onSuppressItem}
          onReplaceItem={onReplaceItem}
          onApplyDay={onApplyDay}
          onApplyAll={onApplyAll}
          applyingItem={applyingItem}
          applyingDay={applyingDay && group.dayIndex === currentDay}
          applyingAll={applyingAll}
        />
      ))}
      
      {/* FALLBACK for malformed proposals */}
      {invalidProposals.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-[#B37E6B] uppercase tracking-wide">
            Format Issues ({invalidProposals.length})
          </p>
          {invalidProposals.map((p, idx) => (
            <ProposalFallback 
              key={p.id || idx}
              content={JSON.stringify(p, null, 2)}
              error="Invalid proposal structure"
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Message Component
function AIMessage({ message, onCopy, onNavigate, onEdit }) {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  
  const handleCopy = () => {
    navigator.clipboard?.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };
  
  const handleSaveEdit = () => {
    if (editContent.trim() && onEdit) {
      onEdit(message.id, editContent.trim());
      setEditing(false);
    }
  };

  const isLong = message.content.length > 500;

  return (
    <div className={`rounded-lg ${
      message.role === 'user' 
        ? 'bg-[#2C2C2C] text-white ml-12' 
        : message.error 
          ? 'bg-[#B37E6B]/10 mr-4' 
          : 'bg-[#F9F8F4] mr-4'
    }`}>
      {/* User Message Header with Edit */}
      {message.role === 'user' && (
        <div className="flex items-center justify-end px-4 pt-2 gap-1">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="p-1 text-white/50 hover:text-white"
              title="Edit message"
            >
              <FileText className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
      
      {/* Assistant Header */}
      {message.role === 'assistant' && (
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C8A85A]" />
            <span className="text-xs text-[#8C8C8C]">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {isLong && (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className="p-1 text-[#8C8C8C] hover:text-[#1A1A1A]"
              >
                {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={handleCopy}
              className="p-1 text-[#8C8C8C] hover:text-[#1A1A1A]"
            >
              {copied ? <Check className="w-4 h-4 text-[#8DA399]" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className={`px-4 pb-3 ${message.role === 'user' && !editing ? 'py-3' : ''}`}>
        {message.role === 'user' ? (
          editing ? (
            <div className="space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full p-2 text-sm bg-[#1A1A1A] text-white rounded border border-white/20 resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setEditing(false); setEditContent(message.content); }}
                  className="px-3 py-1 text-xs text-white/70 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-3 py-1 text-xs bg-[#C8A85A] text-white rounded hover:bg-[#B39049]"
                >
                  Resend
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          )
        ) : collapsed ? (
          <p className="text-sm text-[#8C8C8C]">{message.content.substring(0, 150)}...</p>
        ) : (
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown components={markdownComponents}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        
        {/* Action Button - Deep link to itinerary */}
        {message.action && onNavigate && (
          <button
            onClick={() => onNavigate(message.action.tab, { day: message.action.day })}
            className="mt-3 flex items-center gap-2 text-sm text-[#C8A85A] hover:text-[#B39049] font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            {message.action.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SMART SUGGESTIONS PANEL
// ============================================================
function SmartSuggestionsPanel({ tripId, dayNumber, startDate, tripTypes, travelStyles, destinations, onAddToItinerary }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [timeOfDay, setTimeOfDay] = useState('all');
  const [addingItem, setAddingItem] = useState(null);
  const [expanded, setExpanded] = useState(true);
  
  const categories = [
    { id: 'all', label: 'All', icon: Sparkles },
    { id: 'activities', label: 'Activities', icon: MapIcon },
    { id: 'dining', label: 'Dining', icon: Utensils },
    { id: 'experiences', label: 'Experiences', icon: Eye },
  ];
  
  const timeSlots = [
    { id: 'all', label: 'Any Time' },
    { id: 'morning', label: 'Morning' },
    { id: 'afternoon', label: 'Afternoon' },
    { id: 'evening', label: 'Evening' },
  ];
  
  const fetchSuggestions = async () => {
    if (!tripId || !dayNumber) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/smart-suggestions`, {
        day_number: dayNumber,
        category: category,
        time_of_day: timeOfDay
      });
      setSuggestions(res.data?.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      toast.error('Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddToItinerary = async (suggestion) => {
    setAddingItem(suggestion.name);
    try {
      // Map time_slot to section for SmartItineraryBuilder
      const timeBlockMap = {
        'morning': 'morning',
        'afternoon': 'afternoon', 
        'evening': 'evening',
        'flexible': 'morning'
      };
      
      // Create itinerary item with fields matching SmartItineraryBuilder schema
      const item = {
        trip_id: tripId,
        day_number: dayNumber,
        name: suggestion.name,
        type: suggestion.type || 'activity',
        section: timeBlockMap[suggestion.time_slot] || 'morning',
        time_block: timeBlockMap[suggestion.time_slot] || 'morning',
        duration: suggestion.duration,
        highlight: suggestion.description, // Maps to highlight field
        notes: suggestion.why_perfect || '',
        status: 'proposed',
        source: 'smart_suggestions',
        // Enhanced fields - mapped to SmartItineraryBuilder fields
        price_per_person: suggestion.price_per_person,
        price_amount: suggestion.price_per_person ? 
          parseInt(suggestion.price_per_person.replace(/[^0-9]/g, '')) || null : null,
        currency: 'USD',
        website_url: suggestion.website, // SmartItineraryBuilder uses website_url
        image_url: suggestion.image_url,
        address: suggestion.address,
        phone: suggestion.phone,
        hours: suggestion.hours,
        reservation_required: suggestion.reservation_required || false,
        why_perfect: suggestion.why_perfect,
        price_tier: suggestion.price_tier
      };
      
      await axios.post(`${API}/travels/itinerary-items`, item);
      toast.success(`Added "${suggestion.name}" to Day ${dayNumber} (${timeBlockMap[suggestion.time_slot] || 'morning'})`);
      
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.name !== suggestion.name));
      
      // Callback to parent
      onAddToItinerary?.(item);
    } catch (err) {
      console.error('Failed to add to itinerary:', err);
      toast.error('Failed to add to itinerary');
    } finally {
      setAddingItem(null);
    }
  };
  
  const getTypeIcon = (type) => {
    switch (type) {
      case 'restaurant': return Utensils;
      case 'experience': return Eye;
      case 'tour': return MapIcon;
      case 'relaxation': return Clock;
      default: return Sparkles;
    }
  };
  
  const getPriceTierColor = (tier) => {
    switch (tier) {
      case 'included': return 'text-[#8DA399] bg-[#8DA399]/10';
      case '$': return 'text-[#595959] bg-[#E6E4DE]';
      case '$$': return 'text-[#C8A85A] bg-[#C8A85A]/10';
      case '$$$': return 'text-[#B37E6B] bg-[#B37E6B]/10';
      case '$$$$': return 'text-[#1A1A1A] bg-[#1A1A1A]/10';
      default: return 'text-[#8C8C8C] bg-[#F5F1EB]';
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-[#F9F8F4] to-[#F5F1EB] rounded-xl border border-[#E6E4DE] overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-[#F5F1EB]/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C8A85A] to-[#B39049] flex items-center justify-center">
            <Lightbulb className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-sm text-[#1A1A1A]">Smart Suggestions</h3>
            <p className="text-xs text-[#8C8C8C]">AI-powered ideas for Day {dayNumber}</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#8C8C8C] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      
      {expanded && (
        <div className="p-3 pt-0 space-y-3">
          {/* Filter Row */}
          <div className="flex flex-wrap gap-2">
            {/* Category Pills */}
            <div className="flex gap-1 flex-1">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${
                      category === cat.id 
                        ? 'bg-[#1A1A1A] text-white' 
                        : 'bg-white text-[#595959] hover:bg-[#E6E4DE]'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="hidden sm:inline">{cat.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Time Dropdown */}
            <select
              value={timeOfDay}
              onChange={(e) => setTimeOfDay(e.target.value)}
              className="text-xs px-2 py-1 rounded-full bg-white border border-[#E6E4DE] text-[#595959]"
            >
              {timeSlots.map(slot => (
                <option key={slot.id} value={slot.id}>{slot.label}</option>
              ))}
            </select>
            
            {/* Generate Button */}
            <button
              onClick={fetchSuggestions}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1 text-xs rounded-full bg-[#C8A85A] text-white hover:bg-[#B39049] disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3" />
              )}
              Generate
            </button>
          </div>
          
          {/* Suggestions List */}
          {suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((suggestion, idx) => {
                const TypeIcon = getTypeIcon(suggestion.type);
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-lg overflow-hidden border border-[#E6E4DE] hover:border-[#C8A85A]/30 transition-colors"
                  >
                    {/* Image Header */}
                    {suggestion.image_url && (
                      <div className="relative h-24 bg-[#F5F1EB]">
                        <img 
                          src={suggestion.image_url} 
                          alt={suggestion.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          {suggestion.reservation_required && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#B37E6B] text-white">
                              Reservation
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {!suggestion.image_url && (
                            <div className="w-8 h-8 rounded-lg bg-[#F5F1EB] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <TypeIcon className="w-4 h-4 text-[#C8A85A]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {suggestion.image_url && (
                                <div className="w-5 h-5 rounded bg-[#F5F1EB] flex items-center justify-center flex-shrink-0">
                                  <TypeIcon className="w-3 h-3 text-[#C8A85A]" />
                                </div>
                              )}
                              <h4 className="font-medium text-sm text-[#1A1A1A] truncate">{suggestion.name}</h4>
                            </div>
                            <p className="text-xs text-[#8C8C8C] line-clamp-2 mt-1">{suggestion.description}</p>
                            
                            {/* Price per person - Prominent */}
                            {suggestion.price_per_person && (
                              <div className="flex items-center gap-1 mt-2">
                                <DollarSign className="w-3 h-3 text-[#8DA399]" />
                                <span className="text-xs font-medium text-[#1A1A1A]">
                                  {suggestion.price_per_person}
                                </span>
                                <span className="text-[10px] text-[#8C8C8C]">per person</span>
                              </div>
                            )}
                            
                            {/* Meta info */}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="flex items-center gap-1 text-[10px] text-[#8C8C8C]">
                                <Clock className="w-3 h-3" />
                                {suggestion.duration || '1-2 hrs'}
                              </span>
                              <span className="flex items-center gap-1 text-[10px] text-[#8C8C8C]">
                                <Calendar className="w-3 h-3" />
                                {suggestion.time_slot || 'Flexible'}
                              </span>
                              {suggestion.price_tier && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getPriceTierColor(suggestion.price_tier)}`}>
                                  {suggestion.price_tier}
                                </span>
                              )}
                            </div>
                            
                            {/* Address */}
                            {suggestion.address && (
                              <p className="text-[10px] text-[#8C8C8C] mt-1.5 flex items-start gap-1">
                                <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span className="line-clamp-1">{suggestion.address}</span>
                              </p>
                            )}
                            
                            {/* Why perfect */}
                            {suggestion.why_perfect && (
                              <p className="text-[10px] text-[#C8A85A] mt-1.5 italic">
                                ✨ {suggestion.why_perfect}
                              </p>
                            )}
                            
                            {/* Website Link */}
                            {suggestion.website && (
                              <a 
                                href={suggestion.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[10px] text-[#C8A85A] hover:text-[#B39049] mt-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3 h-3" />
                                Visit Official Website
                              </a>
                            )}
                          </div>
                        </div>
                        
                        {/* Add Button */}
                        <button
                          onClick={() => handleAddToItinerary(suggestion)}
                          disabled={addingItem === suggestion.name}
                          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-[#8DA399] text-white hover:bg-[#7A9389] disabled:opacity-50 transition-colors"
                        >
                          {addingItem === suggestion.name ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                          Add
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !loading ? (
            <div className="text-center py-6">
              <Lightbulb className="w-8 h-8 text-[#E6E4DE] mx-auto mb-2" />
              <p className="text-xs text-[#8C8C8C]">
                Click "Generate" for AI-powered suggestions
              </p>
              <p className="text-[10px] text-[#B7B0A4] mt-1">
                Based on {destinations || 'your destination'} • {(tripTypes || []).join(', ') || 'Leisure'}
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <Loader2 className="w-6 h-6 text-[#C8A85A] animate-spin mx-auto mb-2" />
              <p className="text-xs text-[#8C8C8C]">Generating suggestions...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Trip Context Panel
// ============================================================
// ENHANCED TRIP CONTEXT PANEL - Deep links to ALL tabs
// ============================================================
function TripContextPanel({ context, tripId, onRefresh, loading, onNavigate }) {
  const [expanded, setExpanded] = useState(false); // Default to summary (collapsed) view
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [exportingCalendar, setExportingCalendar] = useState(false);
  
  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh || !tripId) return;
    
    const interval = setInterval(() => {
      onRefresh?.();
      setLastUpdated(new Date());
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, tripId, onRefresh]);
  
  // Format date to MM-DD-YYYY (parse as local to avoid timezone issues)
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      let date;
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const parts = dateStr.split('T')[0].split('-');
        date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime())) return dateStr;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };
  
  // Format last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return null;
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastUpdated.toLocaleTimeString();
  };
  
  // Navigate to specific tab
  const navigateToTab = (tab, section = null) => {
    onNavigate?.(tab, section);
  };
  
  // Export to Google Calendar
  const exportToCalendar = async () => {
    if (!tripId) return;
    setExportingCalendar(true);
    
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/calendar/export`);
      const { ical, filename, events_count } = res.data;
      
      if (!ical) {
        toast.error('No itinerary data to export');
        return;
      }
      
      // Create blob and download
      const blob = new Blob([ical], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'trip_itinerary.ics');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${events_count} events to calendar file`);
    } catch (e) {
      console.error('Calendar export failed:', e);
      toast.error('Failed to export calendar');
    } finally {
      setExportingCalendar(false);
    }
  };

  if (!context) return null;
  
  // Extract all data from context
  const trip = context.trip || {};
  const travelers = context.travelers || [];
  const logistics = context.logistics || {};
  const itinerary = context.itinerary || {};
  const budget = context.budget || {};
  const agreements = context.agreements || {};
  const confirmedDecisions = context.confirmed_decisions || [];

  return (
    <Card className="border-[#E6E4DE] overflow-hidden">
      {/* Header - Always visible */}
      <div className="p-3 bg-[#F9F8F4] border-b border-[#E6E4DE]">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-left flex-1"
          >
            <Eye className="w-4 h-4 text-[#C8A85A]" />
            <span className="text-sm font-medium text-[#1A1A1A]">Trip Context</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />}
          </button>
          
          <div className="flex items-center gap-2">
            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-1.5 rounded-lg transition-colors ${
                autoRefresh ? 'bg-[#8DA399]/20 text-[#8DA399]' : 'bg-[#F5F1EB] text-[#8C8C8C]'
              }`}
              title={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
            </button>
            
            {/* Manual refresh */}
            <button
              onClick={() => {
                onRefresh?.();
                setLastUpdated(new Date());
              }}
              disabled={loading}
              className="p-1.5 rounded-lg hover:bg-[#F5F1EB] text-[#8C8C8C] transition-colors"
              title="Refresh now"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
        
        {/* Last updated timestamp */}
        {lastUpdated && (
          <p className="text-[10px] text-[#8C8C8C] mt-1">
            Updated {formatLastUpdated()}
          </p>
        )}
        
        {/* Summary View - Shows when collapsed */}
        {!expanded && context && (
          <div className="mt-2 pt-2 border-t border-[#E6E4DE]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#595959]">
              <span className="flex items-center gap-1">
                <MapIcon className="w-3 h-3" />
                {getTripDestinationsDisplay(trip) || trip.destinations || 'No destination'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDateUtil(getTripStartDate(trip))} - {formatDateUtil(getTripEndDate(trip))}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {travelers.length || trip.num_travelers || 1} travelers
              </span>
              {budget.total > 0 && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  ${budget.total?.toLocaleString() || 0}
                </span>
              )}
            </div>
            <button 
              onClick={() => setExpanded(true)}
              className="text-[10px] text-[#C8A85A] hover:underline mt-1"
            >
              Click to expand full context →
            </button>
          </div>
        )}
      </div>
      
      {/* Expanded Content - Deep links to ALL tabs */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <CardContent className="p-3 space-y-4">
              {/* ==================== TRIP DETAILS ==================== */}
              <div className="space-y-2">
                <button 
                  onClick={() => navigateToTab('trip_details')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <MapIcon className="w-3 h-3" /> Trip Details
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 space-y-1">
                  <p className="text-sm font-medium text-[#1A1A1A]">
                    {trip.name || trip.internal_trip_name || 'Unnamed Trip'}
                  </p>
                  <p className="text-xs text-[#595959]">{getTripDestinationsDisplay(trip) || trip.destinations || 'No destination set'}</p>
                  
                  {/* Dates in MM-DD-YYYY format */}
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="w-3 h-3 text-[#8C8C8C]" />
                    <span className="text-[#595959]">
                      {formatDateUtil(getTripStartDate(trip))} → {formatDateUtil(getTripEndDate(trip))}
                    </span>
                  </div>
                  
                  {/* Trip type & style */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {trip.trip_type && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C8A85A]/10 text-[#C8A85A]">
                        {trip.trip_type}
                      </span>
                    )}
                    {trip.is_international && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#5B9BD5]/10 text-[#5B9BD5]">
                        International
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* ==================== TRAVELERS ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('trip_details', 'travelers')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <Users className="w-3 h-3" /> Travelers ({travelers.length || trip.num_travelers || 0})
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 space-y-1">
                  {travelers.length > 0 ? (
                    travelers.slice(0, 4).map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#595959]">{t.name || t.legal_full_name}</span>
                        {t.booking_ready === false && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-[#B37E6B]/10 text-[#B37E6B]">
                            Incomplete
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-[#8C8C8C] italic">No travelers linked</p>
                  )}
                  {travelers.length > 4 && (
                    <p className="text-xs text-[#C8A85A]">+{travelers.length - 4} more</p>
                  )}
                </div>
              </div>
              
              {/* ==================== ITINERARY ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('itinerary')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Itinerary
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 space-y-1">
                  <div className="flex gap-3 text-xs">
                    <span className="text-[#595959]">
                      <strong>{itinerary.total_days || 0}</strong> days
                    </span>
                    <span className="text-[#595959]">
                      <strong>{itinerary.total_items || 0}</strong> items
                    </span>
                  </div>
                  
                  {/* Days status */}
                  {itinerary.days_status && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {itinerary.days_completed > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8DA399]/10 text-[#8DA399]">
                          {itinerary.days_completed} complete
                        </span>
                      )}
                      {itinerary.days_in_progress > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C8A85A]/10 text-[#C8A85A]">
                          {itinerary.days_in_progress} in progress
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* ==================== LOGISTICS ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('logistics')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <Plane className="w-3 h-3" /> Logistics
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Plane className="w-3 h-3 text-[#8C8C8C]" />
                      <span className="text-[#595959]">{logistics.flights || 0} flights</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Hotel className="w-3 h-3 text-[#8C8C8C]" />
                      <span className="text-[#595959]">{logistics.hotels || logistics.lodging || 0} hotels</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Car className="w-3 h-3 text-[#8C8C8C]" />
                      <span className="text-[#595959]">{logistics.transfers || 0} transfers</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-3 h-3 text-[#8C8C8C]" />
                      <span className="text-[#595959]">{logistics.reservations || 0} reservations</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* ==================== BUDGET ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('trip_details', 'budget')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Budget
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 space-y-1">
                  {budget.total_budget > 0 ? (
                    <>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#595959]">Total:</span>
                        <span className="font-medium">${(budget.total_budget || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-[#595959]">Spent:</span>
                        <span className={budget.alert_threshold ? 'text-[#B37E6B] font-medium' : ''}>
                          ${(budget.total_spent || 0).toLocaleString()} ({budget.percentage_used?.toFixed(0) || 0}%)
                        </span>
                      </div>
                      
                      {/* Budget alert indicator */}
                      {budget.alert_threshold && (
                        <div className={`text-[10px] px-2 py-1 rounded mt-1 ${
                          budget.alert_threshold === 'over_budget' ? 'bg-[#B37E6B]/10 text-[#B37E6B]' :
                          budget.alert_threshold === 'high_risk' ? 'bg-amber-50 text-amber-600' :
                          'bg-[#C8A85A]/10 text-[#C8A85A]'
                        }`}>
                          {budget.alert_threshold === 'over_budget' ? '⚠️ Over budget' :
                           budget.alert_threshold === 'high_risk' ? '⚠️ High risk (90%+)' :
                           '📊 Approaching budget (80%+)'}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-[#8C8C8C] italic">No budget set</p>
                  )}
                </div>
              </div>
              
              {/* ==================== AGREEMENTS ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('agreements')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Agreements
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${
                      agreements.contract_signed ? 'bg-[#8DA399]/10 text-[#8DA399]' : 'bg-[#F5F5F5] text-[#8C8C8C]'
                    }`}>
                      Contract: {agreements.contract_signed ? '✓' : '○'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded ${
                      agreements.terms_acknowledged ? 'bg-[#8DA399]/10 text-[#8DA399]' : 'bg-[#F5F5F5] text-[#8C8C8C]'
                    }`}>
                      T&C: {agreements.terms_acknowledged ? '✓' : '○'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded ${
                      agreements.card_auth_received ? 'bg-[#8DA399]/10 text-[#8DA399]' : 'bg-[#F5F5F5] text-[#8C8C8C]'
                    }`}>
                      Card: {agreements.card_auth_received ? '✓' : '○'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* ==================== TRIP ESTIMATE / PRICES ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('prices')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Trip Estimate
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 text-xs text-[#595959]">
                  {trip.price_total > 0 ? (
                    <span>Total: <strong>${trip.price_total?.toLocaleString()}</strong></span>
                  ) : (
                    <span className="text-[#8C8C8C] italic">Not calculated</span>
                  )}
                </div>
              </div>
              
              {/* ==================== CLIENT PORTAL ==================== */}
              <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                <button 
                  onClick={() => navigateToTab('client_lounge')}
                  className="flex items-center justify-between w-full group"
                >
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Client Portal
                  </p>
                  <ExternalLink className="w-3 h-3 text-[#C8A85A] opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                
                <div className="pl-4 text-xs">
                  <div className="flex gap-2">
                    <span className={`px-1.5 py-0.5 rounded ${
                      context.client_portal?.published ? 'bg-[#8DA399]/10 text-[#8DA399]' : 'bg-[#F5F5F5] text-[#8C8C8C]'
                    }`}>
                      {context.client_portal?.published ? 'Published' : 'Draft'}
                    </span>
                    {context.client_portal?.unread_messages > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-[#C8A85A]/10 text-[#C8A85A]">
                        {context.client_portal.unread_messages} unread
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* ==================== SYNC TO CALENDAR ==================== */}
              <div className="border-t border-[#E6E4DE] pt-3">
                <Button
                  onClick={exportToCalendar}
                  disabled={exportingCalendar || !itinerary.total_items}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8"
                >
                  {exportingCalendar ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Calendar className="w-3 h-3 mr-1" />
                  )}
                  Sync to Calendar (.ics)
                </Button>
                <p className="text-[10px] text-[#8C8C8C] mt-1 text-center">
                  Download iCal file to import to Google/Apple Calendar
                </p>
              </div>
              
              {/* ==================== RECENT DECISIONS ==================== */}
              {confirmedDecisions.length > 0 && (
                <div className="space-y-2 border-t border-[#E6E4DE] pt-3">
                  <p className="text-xs text-[#8C8C8C] uppercase tracking-wide flex items-center gap-1">
                    <Check className="w-3 h-3" /> Recent AI Decisions
                  </p>
                  <div className="pl-4 space-y-1">
                    {confirmedDecisions.slice(-3).map((d, i) => (
                      <p key={i} className="text-[10px] text-[#8DA399]">
                        ✓ {d.reason || `Updated ${d.field}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// Main AI Assistant Component
export function TripAIAssistant({ tripId, form, startDate, endDate, totalDays: propTotalDays, onNavigate, onGoToItinerary }) {
  const [messages, setMessages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);
  const [contextSnippets, setContextSnippets] = useState([]);
  const [tripContext, setTripContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // BULK APPLY state
  const [bulkApplying, setBulkApplying] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [existingContent, setExistingContent] = useState(null);
  
  // DAY-SCOPED PLANNING state
  const [planningState, setPlanningState] = useState({
    current_working_day: 1,
    total_days: propTotalDays || 0,
    days_completed: [],
    days_in_progress: [],
    locked_days: {}
  });
  const [applyingDay, setApplyingDay] = useState(false);
  const [currentWorkingDay, setCurrentWorkingDay] = useState(1);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Calculate total days from props if available
  const totalDays = propTotalDays || planningState.total_days;

  // Load conversation history on mount
  useEffect(() => {
    if (tripId) {
      loadConversation();
      loadContext();
      loadPendingProposals();
      loadPlanningState();
    }
  }, [tripId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Listen for navigation events from proposals
  useEffect(() => {
    const handleNavigate = (e) => {
      if (onNavigate && e.detail?.tab) {
        onNavigate(e.detail.tab, e.detail.section);
      }
    };
    window.addEventListener('ai-navigate', handleNavigate);
    return () => window.removeEventListener('ai-navigate', handleNavigate);
  }, [onNavigate]);
  
  // Load planning state
  const loadPlanningState = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-planning-state`);
      setPlanningState(res.data);
    } catch (e) {
      console.error('Failed to load planning state:', e);
    }
  };

  const loadConversation = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-memory/messages?limit=50`);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error('Failed to load conversation:', e);
    }
  };

  const loadContext = async () => {
    setContextLoading(true);
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-context`);
      setTripContext(res.data);
    } catch (e) {
      console.error('Failed to load context:', e);
    } finally {
      setContextLoading(false);
    }
  };

  const loadPendingProposals = async () => {
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-proposals`);
      const loadedProposals = res.data.proposals || [];
      
      // Check availability for proposals that haven't been checked recently
      const proposalsToCheck = loadedProposals.filter(p => 
        !p.availability || p.availability === 'unknown'
      );
      
      if (proposalsToCheck.length > 0) {
        // Check availability in background
        checkProposalsAvailability(proposalsToCheck, loadedProposals);
      }
      
      setProposals(loadedProposals);
    } catch (e) {
      console.error('Failed to load proposals:', e);
    }
  };
  
  // Check availability for proposals
  const checkProposalsAvailability = async (toCheck, allProposals) => {
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/check-availability`, {
        proposals: toCheck
      });
      
      const checkedProposals = res.data.proposals || [];
      
      // Merge checked availability back into proposals
      setProposals(prev => {
        const updated = [...prev];
        for (const checked of checkedProposals) {
          const idx = updated.findIndex(p => p.id === checked.id);
          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              availability: checked.availability,
              confidence: checked.confidence,
              urgency: checked.urgency,
              why_recommended: checked.why_recommended || updated[idx].why_recommended,
              availability_details: checked.availability_details
            };
          }
        }
        return updated;
      });
    } catch (e) {
      console.error('Failed to check availability:', e);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/travels/ai-assistant`, {
        trip_id: tripId,
        message: userMessage.content,
        include_context: contextSnippets
      });
      
      // Add assistant response
      const assistantMessage = {
        id: `resp-${Date.now()}`,
        role: 'assistant',
        content: res.data.message,
        timestamp: new Date().toISOString(),
        error: res.data.error
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Add any new proposals
      if (res.data.proposals?.length > 0) {
        setProposals(prev => [...prev, ...res.data.proposals]);
      }
      
    } catch (e) {
      const errorMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: e.response?.data?.message || 'Failed to get response. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };
  
  // Edit and resend a user message
  const editAndResend = async (messageId, newContent) => {
    if (!newContent.trim() || loading) return;
    
    // Find the message index
    const msgIndex = messages.findIndex(m => m.id === messageId);
    if (msgIndex === -1) return;
    
    // Remove all messages after the edited message (and their responses)
    const truncatedMessages = messages.slice(0, msgIndex);
    
    // Create updated user message
    const updatedUserMessage = {
      id: `edit-${Date.now()}`,
      role: 'user',
      content: newContent.trim(),
      timestamp: new Date().toISOString(),
      edited: true
    };
    
    setMessages([...truncatedMessages, updatedUserMessage]);
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/travels/ai-assistant`, {
        trip_id: tripId,
        message: newContent.trim(),
        include_context: contextSnippets
      });
      
      const assistantMessage = {
        id: `resp-${Date.now()}`,
        role: 'assistant',
        content: res.data.message,
        timestamp: new Date().toISOString(),
        error: res.data.error
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      if (res.data.proposals?.length > 0) {
        setProposals(prev => [...prev, ...res.data.proposals]);
      }
      
      toast.success('Message updated and resent');
    } catch (e) {
      const errorMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: e.response?.data?.message || 'Failed to get response. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const applyProposal = async (proposalId) => {
    setApplying(proposalId);
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/${proposalId}/apply`);
      toast.success('Change applied to trip');
      setProposals(prev => prev.filter(p => p.id !== proposalId));
      loadContext(); // Refresh context after change
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply change');
    } finally {
      setApplying(null);
    }
  };

  const rejectProposal = async (proposalId) => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/${proposalId}/reject`);
      setProposals(prev => prev.filter(p => p.id !== proposalId));
    } catch (e) {
      console.error('Failed to reject proposal:', e);
    }
  };
  
  // ADVISOR OVERRIDE HANDLERS
  const handlePromoteProposal = async (proposalId) => {
    // Toggle promoted status locally (persists for trip session)
    setProposals(prev => prev.map(p => 
      p.id === proposalId ? { ...p, promoted: !p.promoted } : p
    ));
    toast.success('Proposal pinned to top');
    
    // Optionally persist to backend
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/${proposalId}/promote`);
    } catch (e) {
      console.error('Failed to persist promotion:', e);
    }
  };
  
  const handleSuppressProposal = async (proposalId) => {
    // Mark as suppressed (hidden, won't resurface)
    setProposals(prev => prev.map(p => 
      p.id === proposalId ? { ...p, suppressed: true } : p
    ));
    toast.success('Proposal hidden');
    
    // Persist to backend
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/${proposalId}/suppress`);
    } catch (e) {
      console.error('Failed to persist suppression:', e);
    }
  };
  
  const handleReplaceProposal = async (proposalId) => {
    // Request alternatives for this proposal
    const proposal = proposals.find(p => p.id === proposalId);
    if (!proposal) return;
    
    toast.info('Requesting alternatives...');
    setInput(`Please suggest alternatives to "${proposal.data?.name || 'this item'}" for Day ${proposal.data?.day || 1}. Consider similar quality, location, and price range.`);
    textareaRef.current?.focus();
  };
  
  // BULK APPLY FUNCTIONS
  const handleBulkApplyClick = async () => {
    // Apply all pending proposals
    const pendingProposals = proposals.filter(p => p.status === 'pending' || !p.status);
    if (pendingProposals.length === 0) {
      toast.error('No proposals to apply');
      return;
    }
    
    const proposalIds = pendingProposals.map(p => p.id);
    
    // Check for existing itinerary content
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-proposals/check-conflicts`);
      setExistingContent(res.data.details);
      
      if (res.data.has_existing_content) {
        // Show conflict resolution modal
        setShowConflictModal(true);
      } else {
        // No conflicts, apply directly with append mode
        await executeBulkApply('append', proposalIds);
      }
    } catch (e) {
      console.error('Failed to check conflicts:', e);
      // If check fails, proceed with append mode
      await executeBulkApply('append', proposalIds);
    }
  };
  
  const executeBulkApply = async (conflictMode, proposalIds = null) => {
    const idsToApply = proposalIds || proposals.filter(p => p.status === 'pending' || !p.status).map(p => p.id);
    
    setBulkApplying(true);
    setShowConflictModal(false);
    
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/bulk-apply`, {
        proposal_ids: idsToApply,
        conflict_mode: conflictMode
      });
      
      if (res.data.success) {
        toast.success(`Successfully applied ${res.data.applied_count} items to itinerary`);
        
        // Clear applied proposals from state
        setProposals(prev => prev.filter(p => !idsToApply.includes(p.id)));
        
        // Refresh context and planning state
        loadContext();
        loadPlanningState();
        
        // Navigate to itinerary tab to show results
        if (onNavigate) {
          onNavigate('itinerary');
        }
        
        // Add system message about the applied changes
        const systemMessage = {
          id: `system-${Date.now()}`,
          role: 'assistant',
          content: `✅ **Itinerary Updated**\n\nI've applied ${res.data.applied_count} items to your trip itinerary. The changes have been saved to:\n\n${res.data.applied_items.map(item => `- **${item.name}** → ${item.section}`).join('\n')}\n\nWould you like me to optimize the timing, suggest reservations, or identify any gaps in the current plan?`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, systemMessage]);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to apply changes';
      toast.error(errorMsg);
      console.error('Bulk apply error:', e);
    } finally {
      setBulkApplying(false);
    }
  };
  
  // APPLY DAY - Primary action for day-scoped planning
  const handleApplyDay = async (dayNumber = null) => {
    const targetDay = dayNumber || planningState.current_working_day;
    const dayProposals = proposals.filter(p => p.data?.day === targetDay);
    
    if (dayProposals.length === 0) {
      toast.error(`No proposals for Day ${targetDay} to apply`);
      return;
    }
    
    setApplyingDay(true);
    
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/apply-day`, {
        day_number: targetDay
      });
      
      if (res.data.success) {
        toast.success(`Day ${targetDay} approved with ${res.data.applied_count} items`);
        
        // Remove applied proposals from state
        setProposals(prev => prev.filter(p => p.data?.day !== targetDay));
        
        // Update planning state
        loadPlanningState();
        loadContext();
        
        // Add system message with deep link to itinerary
        const systemMessage = {
          id: `system-${Date.now()}`,
          role: 'assistant',
          content: `✅ **Day ${targetDay} Locked**\n\n${res.data.applied_count} items have been added to your itinerary.\n\n${res.data.applied_items.map(item => `- **${item.name}** (${item.type})`).join('\n')}\n\n${res.data.next_working_day ? `Ready to plan **Day ${res.data.next_working_day}**. What would you like to focus on?` : 'All days are now planned!'}`,
          timestamp: new Date().toISOString(),
          action: {
            label: `View Day ${targetDay} in Itinerary`,
            tab: 'itinerary',
            day: targetDay
          }
        };
        setMessages(prev => [...prev, systemMessage]);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to apply day';
      toast.error(errorMsg);
      console.error('Apply day error:', e);
    } finally {
      setApplyingDay(false);
    }
  };
  
  // Lock a day to prevent AI changes
  const handleLockDay = async (dayNumber) => {
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/lock-day/${dayNumber}`);
      toast.success(`Day ${dayNumber} locked`);
      loadPlanningState();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to lock day');
    }
  };
  
  // Unlock a day for re-planning
  const handleUnlockDay = async (dayNumber) => {
    if (!confirm(`Unlock Day ${dayNumber} for re-planning? This allows you to modify the approved items.`)) return;
    
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/unlock-day/${dayNumber}`);
      toast.success(`Day ${dayNumber} unlocked for editing`);
      loadPlanningState();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlock day');
    }
  };

  const clearConversation = async () => {
    if (!confirm('Clear conversation history? Confirmed decisions will be preserved.')) return;
    
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-memory/clear?keep_decisions=true`);
      setMessages([]);
      toast.success('Conversation cleared');
    } catch (e) {
      toast.error('Failed to clear conversation');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-testid="trip-ai-assistant">
      {/* Conflict Resolution Modal */}
      <ConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onConfirm={executeBulkApply}
        existingContent={existingContent}
        proposalCount={proposals.filter(p => p.status === 'pending' || !p.status).length}
        loading={bulkApplying}
      />
      
      {/* Main Chat Panel - Mobile optimized with dynamic height */}
      <div className="lg:col-span-2 bg-white rounded-xl border border-[rgba(0,0,0,0.06)] flex flex-col min-h-[500px] md:min-h-[600px] lg:h-[750px] relative">
        {/* Header with Day Progress - Mobile compact */}
        <div className="p-3 md:p-4 border-b border-[rgba(0,0,0,0.06)] flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="font-serif text-base md:text-lg text-[#1A1A1A] truncate">Trip Planning Assistant</h2>
              <p className="text-xs text-[#8C8C8C]">
                {messages.length} messages
              </p>
            </div>
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className={`p-2 ${showHistory ? 'bg-[#F5F1EB]' : ''}`}
                className={showHistory ? 'bg-[#F5F1EB]' : ''}
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearConversation}
                className="text-[#8C8C8C] hover:text-[#B37E6B]"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Day Navigator - Mobile Optimized */}
          {totalDays > 0 && (
            <div className="mt-3 p-3 bg-[#F9F8F4] rounded-lg space-y-3">
              {/* Header with day info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#1A1A1A]">
                    Day {currentWorkingDay}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#C8A85A]/10 text-[#C8A85A]">
                    {totalDays} days total
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {/* Lock/Unlock current day */}
                  <button
                    onClick={() => planningState.locked_days?.[currentWorkingDay] 
                      ? handleUnlockDay(currentWorkingDay)
                      : handleLockDay(currentWorkingDay)
                    }
                    className={`p-1.5 rounded transition-colors ${
                      planningState.locked_days?.[currentWorkingDay]
                        ? 'bg-[#8DA399]/10 text-[#8DA399]'
                        : 'text-[#8C8C8C] hover:bg-[#E6E4DE]'
                    }`}
                    title={planningState.locked_days?.[currentWorkingDay] ? 'Unlock day' : 'Lock day'}
                  >
                    {planningState.locked_days?.[currentWorkingDay] ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <Unlock className="w-4 h-4" />
                    )}
                  </button>
                  {/* Go to Itinerary */}
                  {onGoToItinerary && (
                    <button
                      onClick={() => onGoToItinerary(currentWorkingDay)}
                      className="p-1.5 rounded text-[#8C8C8C] hover:bg-[#E6E4DE] transition-colors"
                      title="View in Itinerary"
                    >
                      <Calendar className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Day selector strip - Scrollable on mobile */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentWorkingDay(Math.max(1, currentWorkingDay - 1))}
                  disabled={currentWorkingDay <= 1}
                  className="p-1 rounded text-[#8C8C8C] hover:bg-[#E6E4DE] disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex-1 overflow-x-auto scrollbar-hide">
                  <div className="flex gap-1">
                    {Array.from({ length: totalDays }, (_, i) => i + 1).map(day => {
                      const isLocked = planningState.locked_days?.[day] || planningState.days_completed?.includes(day);
                      const isCurrent = day === currentWorkingDay;
                      const hasProposals = proposals.some(p => (p.data?.day === day || p.day_number === day) && !p.applied && !p.rejected);
                      const dayDate = startDate ? getDayDate(startDate, day) : null;
                      
                      return (
                        <button
                          key={day}
                          onClick={() => setCurrentWorkingDay(day)}
                          className={`flex-shrink-0 flex flex-col items-center px-2 py-1.5 rounded-lg transition-all min-w-[48px] ${
                            isCurrent
                              ? 'bg-[#1A1A1A] text-white'
                              : isLocked 
                                ? 'bg-[#8DA399]/10 text-[#8DA399]' 
                                : hasProposals 
                                  ? 'bg-[#C8A85A]/10 text-[#C8A85A] hover:bg-[#C8A85A]/20'
                                  : 'bg-white text-[#595959] hover:bg-[#E6E4DE]'
                          }`}
                          title={isLocked ? `Day ${day} (Locked)` : `Day ${day}`}
                        >
                          <span className="text-xs font-medium">D{day}</span>
                          {dayDate && (
                            <span className={`text-[10px] ${isCurrent ? 'text-white/70' : 'text-[#8C8C8C]'}`}>
                              {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          <div className="flex items-center gap-0.5 mt-0.5">
                            {isLocked && <Lock className="w-2.5 h-2.5" />}
                            {hasProposals && !isLocked && <Sparkles className="w-2.5 h-2.5" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <button
                  onClick={() => setCurrentWorkingDay(Math.min(totalDays, currentWorkingDay + 1))}
                  disabled={currentWorkingDay >= totalDays}
                  className="p-1 rounded text-[#8C8C8C] hover:bg-[#E6E4DE] disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* Current day date display */}
              {startDate && (
                <div className="text-xs text-[#8C8C8C] text-center">
                  {formatDayHeader(startDate, currentWorkingDay)}
                </div>
              )}
            </div>
          )}
          
          {/* No dates set message */}
          {(!totalDays || totalDays === 0) && (
            <div className="mt-3 p-3 bg-[#FEF3CD] rounded-lg border border-[#F5C948]/30">
              <div className="flex items-center gap-2 text-sm text-[#8B6914]">
                <AlertCircle className="w-4 h-4" />
                <span>Set trip dates in Trip Details to enable day-by-day planning</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Messages - Scrollable area with padding for sticky input */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-[220px]">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-[#C8A85A]/30 mx-auto mb-4" />
              <h3 className="font-medium text-[#1A1A1A] mb-2">Ready to help plan this trip</h3>
              <p className="text-sm text-[#8C8C8C] max-w-sm mx-auto">
                Ask for hotel recommendations, restaurant suggestions, activity ideas, or help with logistics.
              </p>
              
              {/* Quick prompts */}
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {[
                  "What hotels would you recommend?",
                  "Suggest restaurants for this trip",
                  "What's missing from this itinerary?",
                  "Help me optimize the routing"
                ].map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(prompt)}
                    className="px-3 py-1.5 text-xs bg-[#F5F1EB] text-[#595959] rounded-full hover:bg-[#EEE9E2]"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <AIMessage key={msg.id} message={msg} onNavigate={onNavigate} onEdit={editAndResend} />
              ))}
              {loading && (
                <div className="flex items-center gap-2 p-4 bg-[#F9F8F4] rounded-lg mr-4">
                  <Loader2 className="w-4 h-4 animate-spin text-[#C8A85A]" />
                  <span className="text-sm text-[#8C8C8C]">Thinking...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        {/* Input - STICKY, always visible, larger font */}
        <div className="sticky bottom-0 p-4 border-t border-[rgba(0,0,0,0.06)] bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
          <ContextSnippets selected={contextSnippets} onChange={setContextSnippets} />
          
          <div className="flex gap-3">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about hotels, restaurants, activities, logistics..."
              className="flex-1 min-h-[80px] max-h-[180px] bg-[#F5F1EB] border-[#E6E4DE] resize-none text-base leading-relaxed"
              rows={3}
              style={{ fontSize: '16px' }}
            />
            <Button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-[#C8A85A] hover:bg-[#B39049] h-auto px-5 self-end mb-1"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          
          <p className="text-xs text-[#8C8C8C] mt-2">
            Enter to send • Shift+Enter for new line • AI can read all trip tabs and propose changes
          </p>
        </div>
      </div>
      
      {/* RIGHT PANEL - Proposals (independently scrollable) */}
      <div className="space-y-4 max-h-[750px] overflow-y-auto pr-1">
        {/* Trip Context - ENHANCED with deep links to ALL tabs */}
        <TripContextPanel 
          context={tripContext}
          tripId={tripId}
          onRefresh={loadContext}
          loading={contextLoading}
          onNavigate={onNavigate}
        />
        
        {/* SMART SUGGESTIONS - AI-powered activity recommendations */}
        {totalDays > 0 && (
          <SmartSuggestionsPanel
            tripId={tripId}
            dayNumber={currentWorkingDay}
            startDate={startDate}
            tripTypes={form?.trip_types || tripContext?.trip_types || []}
            travelStyles={form?.travel_styles || tripContext?.travel_styles || []}
            destinations={form?.destinations || tripContext?.destinations || ''}
            onAddToItinerary={() => {
              // Refresh proposals after adding item
              loadPendingProposals();
            }}
          />
        )}
        
        {/* PROPOSALS PANEL - Structured, separate from chat layer */}
        <ProposalsPanel
          proposals={proposals}
          planningState={planningState}
          onApproveItem={(proposalId, proposal) => applyProposal(proposalId)}
          onRejectItem={rejectProposal}
          onEditItem={(proposal) => {
            // TODO: Open edit modal
            toast.info('Edit functionality coming soon');
          }}
          onPromoteItem={handlePromoteProposal}
          onSuppressItem={handleSuppressProposal}
          onReplaceItem={handleReplaceProposal}
          onApplyDay={handleApplyDay}
          onApplyAll={handleBulkApplyClick}
          applyingItem={applying}
          applyingDay={applyingDay}
          applyingAll={bulkApplying}
        />
        
        {/* Quick Actions */}
        <Card className="border-[#E6E4DE]">
          <CardContent className="p-4">
            <p className="text-xs text-[#8C8C8C] uppercase tracking-wide mb-3">Quick Questions</p>
            <div className="space-y-2">
              {[
                { q: "What's next?", icon: ArrowRight },
                { q: "Any blockers?", icon: AlertCircle },
                { q: "Suggest improvements", icon: Sparkles }
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => setInput(item.q)}
                  className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg hover:bg-[#F5F1EB] transition-colors"
                >
                  <item.icon className="w-4 h-4 text-[#C8A85A]" />
                  {item.q}
                </button>
              ))}
            </div>
            
            {/* P1 ENHANCEMENT: Route Optimization Quick Action */}
            {planningState.current_working_day && (
              <div className="mt-3 pt-3 border-t border-[#E6E4DE]">
                <button
                  onClick={async () => {
                    const day = planningState.current_working_day;
                    setInput(`Optimize the route for Day ${day} - analyze all items and suggest the most efficient order based on locations, opening hours, and meal timing.`);
                  }}
                  className="w-full flex items-center gap-2 p-2 text-sm text-left rounded-lg bg-[#C8A85A]/10 hover:bg-[#C8A85A]/20 transition-colors"
                  data-testid="quick-optimize-route-btn"
                >
                  <ListChecks className="w-4 h-4 text-[#C8A85A]" />
                  <span className="text-[#C8A85A] font-medium">Optimize Day {planningState.current_working_day} Route</span>
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default TripAIAssistant;
